<template>
    <x-container fluid>
        <x-layout>
            <x-flex xs12 class="right">
                <x-card class="panel">
                    <x-card-title class="justify-space-between align-start">
                        <h3>Market response sheet</h3>

                        <div class="d-flex">
                            <x-menu v-if="!isEditActive" open-on-hover offset-y>
                                <template v-slot:activator="{ on }">
                                    <x-btn
                                        color="tertiary"
                                        v-on="on"
                                        :loading="isDocumentCurrentlyDownloading"
                                    >
                                        Download

                                        <i class="material-icons ml-2">expand_more</i>
                                    </x-btn>
                                </template>

                                <x-list>
                                    <x-list-tile @click="downloadMarketSheet('pdf')">
                                        <x-list-tile-title>
                                            <i class="material-icons mr-2">picture_as_pdf</i> PDF
                                        </x-list-tile-title>
                                    </x-list-tile>

                                    <x-list-tile
                                        @click="downloadMarketSheet('xlsx')"
                                    >
                                        <x-list-tile-title>
                                            <i class="material-icons mr-2">table_view</i> Excel
                                        </x-list-tile-title>
                                    </x-list-tile>
                                </x-list>
                            </x-menu>
                        </div>
                    </x-card-title>

                    <x-card-text>
                        <x-data-table
                            class="no-row-hover double-rows align-middle"
                            :class="{ 'unset-align': isEditActive }"
                            :headers="headers"
                            :items="marketSheetValues"
                            :sort-desc="[false]"
                            hide-actions
                        >
                            <template v-slot:no-data>
                                <p class="error-message text-xs-center">
                                    No records found
                                </p>
                            </template>

                            <template v-slot:items="props">
                                <tr :class="props.item.status">
                                    <!-- Insurer -->
                                    <td>
                                        <strong>{{ props.item.insurer.name }}</strong>
                                    </td>

                                    <!-- Indicated line size -->
                                    <td class="text-xs-right">
                                        <template v-if="isEditActive">
                                            <div v-if="getIsEditableStatus(props.item.status)" class="d-flex">
                                                <x-select
                                                    :items="currencies"
                                                    v-model="props.item.indicated_line_size_currency_id"
                                                    class="line-size-currency"
                                                    label=""
                                                    placeholder=" "
                                                    prefix=""
                                                    item-text="code"
                                                    item-value="id"
                                                    append-icon="keyboard_arrow_down"
                                                    hide-details
                                                    @change="lineSizeCurrencyChanged(props.item)"
                                                />

                                                <x-currency-text-field
                                                    v-model="props.item.indicated_line_size"
                                                    class="line-size-amount"
                                                    placeholder=""
                                                    prefix=""
                                                    outline
                                                    hide-details
                                                    no-margin
                                                    :optional="true"
                                                />
                                            </div>

                                            <x-currency-text-field
                                                v-else
                                                :value="props.item.line_size_amount"
                                                :disabled="true"
                                                class="quote-price"
                                                :prefix="props.item.indicated_line_size_currency?.code || risk.currency.code"
                                                hide-details
                                                no-margin
                                                :optional="true"
                                            />
                                        </template>

                                        <template v-else>
                                            <p>
                                                <template v-if="!getIsEditableStatus(props.item.status)">
                                                    {{ props.item?.line_size_amount ?  $options.filters.currency(props.item.line_size_amount, props.item?.indicated_line_size_currency?.code || risk.currency.code) : '' }}
                                                </template>

                                                <template v-else>
                                                    {{ props.item?.indicated_line_size ?  $options.filters.currency(props.item.indicated_line_size, props.item?.indicated_line_size_currency?.code || risk.currency.code) : '' }}
                                                </template>
                                            </p>
                                        </template>
                                    </td>

                                    <!-- Pricing Type -->
                                    <td>
                                        <template v-if="isEditActive">
                                            <x-select
                                                outline
                                                :disabled="!getIsEditableStatus(props.item.status)"
                                                v-model="props.item.type"
                                                :items="offerTypes"
                                                item-value="tag"
                                                item-text="name"
                                                append-icon="keyboard_arrow_down"
                                                hide-details
                                                @change="pricingTypeChanged(props.item)"
                                            />
                                        </template>

                                        <template v-else>
                                            {{ getOfferTypeName(props.item.type) }}
                                        </template>
                                    </td>

                                    <!-- Percent of margin -->
                                    <td>
                                        <template v-if="isEditActive">
                                            <x-text-field
                                                outline
                                                v-model="props.item.percentage_of_margin"
                                                :disabled="!getIsEditableStatus(props.item.status) || !(['percentage_of_gross_margin', 'percentage_of_net_margin'].includes(props.item.type))"
                                                suffix="%"
                                                :rules="[
                                                    v => ruleGreaterThan(v, 0, 'Percentage')
                                                ]"
                                                @input="updatePrice(props.item)"
                                            />
                                        </template>

                                        <template v-else>
                                            <template v-if="['percentage_of_gross_margin', 'percentage_of_net_margin'].includes(props.item.type)">
                                                {{ props.item?.percentage_of_margin | percent }}
                                            </template>

                                            <template v-else>
                                                N/A
                                            </template>
                                        </template>
                                    </td>

                                    <!-- Price -->
                                    <td v-if="isEditActive">
                                        <x-text-field
                                            v-if="['fixed', 'variable', 'retained_percent', 'rate', 'percentage_of_gross_margin', 'percentage_of_net_margin'].includes(props.item.type)"
                                            v-model="props.item.price"
                                            :disabled="isBPSEditDisabled(props.item)"
                                            suffix="BPS"
                                            :rules="[
                                                v => ruleGreaterThan(props.item.line_size_amount, 0, 'Price')
                                            ]"
                                        />

                                        <x-currency-text-field
                                            v-if="props.item.type === 'flat_fee'"
                                            v-model="props.item.flat_fee"
                                            :prefix="riskCurrencyCode"
                                            :disabled="!getIsEditableStatus(props.item.status) || !['flat_fee'].includes(props.item.type)"
                                            :optional="true"
                                            :rules="[
                                                v => ruleNumeric(v, false, false, true),
                                            ]"
                                        />
                                    </td>

                                    <td v-else>
                                        {{ getPrice(props.item) }}
                                    </td>

                                    <!-- Status -->
                                    <td>
                                        <template v-if="isEditActive">
                                            <x-select
                                                outline
                                                v-model="props.item.market_response_status"
                                                :disabled="!getIsEditableStatus(props.item.status)"
                                                :items="offerStatuses"
                                                item-value="tag"
                                                item-text="name"
                                                append-icon="keyboard_arrow_down"
                                                hide-details
                                                :class="{
                                                    'input-small': 'small'
                                                }"
                                            />
                                        </template>

                                        <template v-else>
                                            <status-chip :status="props.item.market_response_status" class="ma-0" />
                                        </template>
                                    </td>

                                    <!-- Feedback -->
                                    <td class="text-xs-center">
                                        <x-btn
                                            color="tertiary"
                                            class="extra-small"
                                            @click="() => openFeedbackModal(props.item.id)"
                                        >
                                            <span>View</span>
                                        </x-btn>

                                        <span class="counter" :class="{ 'white': feedbackCount(props.item) === 0 }">
                                            {{ feedbackCount(props.item) }}
                                        </span>
                                    </td>

                                    <!-- Subjectivities -->
                                    <td class="text-xs-center">
                                        <x-btn
                                            color="tertiary"
                                            class="extra-small"
                                            :readonly="props.item.subjectivities.length === 0"
                                            @click="() => openSubjectivitiesModal(props.item.id)"
                                        >
                                            <span>View</span>
                                        </x-btn>

                                        <span class="counter" :class="{ 'white': props.item.subjectivities.length === 0 }">
                                            {{ props.item.subjectivities.length }}
                                        </span>
                                    </td>

                                    <td class="text-xs-right">
                                        {{  props.item.brokerage }}%
                                    </td>
                                </tr>

                                <tr :class="props.item.status">
                                    <td class="note-cell" :colspan="9">
                                        <p>
                                            <span>Security: {{ props.item.security_name || 'N/A' }}</span><br>
                                            <span>Underwriter: {{ props.item?.insurer_user?.name ? props.item?.insurer_user?.name : 'N/A' }}</span>
                                            <template v-if="props.item.insurer_role"><br><span>Role: {{ props.item.insurer_role }}</span></template>
                                        </p>
                                    </td>
                                </tr>
                            </template>
                        </x-data-table>
                    </x-card-text>
                </x-card>
            </x-flex>
        </x-layout>

        <feedback-modal
            v-if="activeOffer"
            ref="feedbackModal"
            :offer="activeOffer"
            @feedbackAdded="(feedback)  => updateFeedback(feedback)"
        />

        <subjectivities-modal
            v-if="activeOffer"
            ref="subjectivitiesModal"
            :offer="activeOffer"
            :readonly="true"
            @subjectivitiesUpdated="(offer) => subjectivitiesUpdated(offer)"
        />
    </x-container>
</template>

<script>
import StatusChip from '~/components/Shared/UIComponents/StatusChip';
import FeedbackModal from '~/components/Shared/UIComponents/FeedbackModal';
import SubjectivitiesModal from '~/components/Shared/UIComponents/SubjectivitiesModal';
import rules from '~/mixins/rules';

export default {
    mixins: [rules],
    components: {
        StatusChip,
        FeedbackModal,
        SubjectivitiesModal
    },
    props: {
        risk: {
            type: Object,
            required: true,
        }
    },
    data() {
        return {
            headers: [
                {
                    text: 'Insurer',
                    value: 'insurer',
                    sortable: true,
                    width: '10%'
                },
                {
                    text: 'Indicated line size',
                    value: 'indicated_line_size',
                    sortable: false,
                    align: 'right',
                    width: '15%'
                },
                {
                    text: 'Pricing type',
                    value: 'margin_amount',
                    sortable: false,
                    width: '14%'
                },
                {
                    text: '% of margin',
                    value: 'percentage_of_margin',
                    sortable: false,
                    width: '5%'
                },
                {
                    text: 'Price',
                    value: 'margin_amount',
                    sortable: false,
                    width: '10%',
                },
                {
                    text: 'Status',
                    value: 'margin_amount',
                    sortable: false,
                    width: '10%'
                },
                {
                    text: 'Feedback',
                    value: 'margin_amount',
                    sortable: false,
                    align: 'center',
                    width: '10%'
                },
                {
                    text: 'Subjectivities',
                    value: 'margin_amount',
                    sortable: false,
                    align: 'center',
                    width: '10%'
                },
                {
                    text: 'Brokerage',
                    value: 'margin_amount',
                    sortable: false,
                    align: 'right',
                    width: '5%'
                },
            ],
            marketSheetValues: [],
            activeOffer: null,
            isDocumentCurrentlyDownloading: false,
            isEditActive: false,
            currencies: [],
            offerStatuses: [
                {
                    name: 'Accepted',
                    tag: 'accepted'
                },
                {
                    name: 'Interested',
                    tag: 'interested'
                },
                {
                    name: 'Reviewing',
                    tag: 'reviewing'
                },
                {
                    name: 'Declined',
                    tag: 'declined'
                },
                {
                    name: 'Offered',
                    tag: 'offered'
                },
                {
                    name: 'NTU',
                    tag: 'ntu'
                },
                {
                    name: 'Expired',
                    tag: 'expired'
                },
                {
                    name: 'Requested',
                    tag: 'requested'
                },
                {
                    name: 'Firm Ordered',
                    tag: 'firm_ordered'
                },
            ],
            offerTypes: [
                {
                    name: 'Flat fee',
                    tag: 'flat_fee'
                },
                {
                    name: 'Fixed',
                    tag: 'fixed'
                },
                {
                    name: 'Variable',
                    tag: 'variable'
                },
                {
                    name: 'Rate',
                    tag: 'rate'
                },
                {
                    name: '% of Gross Margin',
                    tag: 'percentage_of_gross_margin'
                },
                {
                    name: '% of Net Margin',
                    tag: 'percentage_of_net_margin'
                }
            ],
        }
    },
    computed: {
        isDeclaration() {
            return this.risk.risk_type === "declaration";
        },
        riskCurrencyCode() {
            return this.risk?.currency?.code || '';
        }
    },
    watch: {
        risk: {
            deep: true,
            immediate: true,
            handler() {
                this.parseMarketSheetValues();
            }
        }
    },
    created() {
        axios.get(this.$root.api(`currency`)).then(res => {
            this.currencies = res.data.data;
        })
    },
    methods: {
        feedbackCount(offer) {
            return offer.feedback.length + Boolean(offer.query);
        },
        isBPSEditDisabled(marketResponseOffer) {
            return !this.getIsEditableStatus(marketResponseOffer.status) || !['fixed', 'variable', 'percentage_of_gross_margin', 'percentage_of_net_margin'].includes(marketResponseOffer.type) || Boolean(marketResponseOffer.type == 'percentage_of_net_margin' && this.risk.net_margin);
        },
        calculateMarginPrice(percentage, minimiumPrice) {
            const calculatedPrice = (percentage || 0) / 100 * (this.risk.net_margin || 0);

            return Math.max(calculatedPrice, minimiumPrice || 0);
        },
        updatePrice(marketResponseOffer) {
            if (marketResponseOffer.type === 'percentage_of_net_margin' && this.risk.net_margin) {
                marketResponseOffer.price = this.calculateMarginPrice(marketResponseOffer.percentage_of_margin).toFixed(6);
            }
        },
        getPrice(marketResponseOffer) {
            const {price} = marketResponseOffer;
            switch (marketResponseOffer.type) {
                case "variable":
                case "fixed":
                case "rate":
                    return `${+Number(price) || ''} ${price ? 'BPS' : ''}`;
                case "flat_fee":
                    return marketResponseOffer.flat_fee ? this.$options.filters.currency(marketResponseOffer.flat_fee, this.riskCurrencyCode) : '';
                case "percentage_of_net_margin":
                case "percentage_of_margin":
                case "retained_percent":
                    if (!marketResponseOffer.percentage_of_margin) {
                        return '';
                    }
                    if (!this.risk.net_margin) {
                        return `${+Number(Number(marketResponseOffer.price).toFixed(6))} ${marketResponseOffer.price ? 'BPS' : ''}`;
                    }
                    marketResponseOffer.price = this.calculateMarginPrice(marketResponseOffer.percentage_of_margin, marketResponseOffer.minimum_price);
                    return `${+Number(this.calculateMarginPrice(marketResponseOffer.percentage_of_margin, marketResponseOffer.minimum_price).toFixed(6))} ${marketResponseOffer.price ? 'BPS' : ''}`;
                case "percentage_of_gross_margin":
                    return `${+Number(Number(marketResponseOffer.price).toFixed(6))} ${marketResponseOffer.price ? 'BPS' : ''}`;
            }

            return '';
        },
        parseMarketSheetValues() {
            const activeFirmOrder = this.risk?.firm_orders
                ?.filter(fo => fo.status !== 'abandoned_draft')
                ?.slice(-1)[0];

            this.marketSheetValues = this.risk?.offers?.map(offer => {
                const offerLines = this.isDeclaration
                    ? (this.risk?.lines || []).filter(line => line.offer_id === offer.id)
                    : (this.risk?.firm_orders?.[0]?.firm_order_offers || []).filter(firm_order_offer => firm_order_offer.offer_id === offer.id);

                const security_name = offerLines
                    .map(line => line?.security?.name)
                    .filter(Boolean)
                    .join(', ') || undefined;

                const insurer_role = (this.isDeclaration
                    ? this.risk?.lines?.find(line => line.offer_id === offer.id)?.insurer_role
                    : activeFirmOrder?.firm_order_offers?.find(firm_order_offer => firm_order_offer.offer_id === offer.id)?.insurer_role) || null;

                return {
                    ...offer,
                    security_name,
                    insurer_role,
                };
            });
        },
        pricingTypeChanged(item) {
            item.price = null;
            item.percentage_of_margin = null;
        },
        lineSizeCurrencyChanged(item) {
            item.indicated_line_size_currency = this.currencies.find(currency => currency.id === item.indicated_line_size_currency_id);
        },
        getOfferTypeName(type) {
            switch (type) {
                case 'rate':
                    return 'Rate';
                case 'flat_fee':
                    return 'Flat fee';
                case 'percentage_of_margin':
                    return '% of Net Margin';
                case 'retained_percent':
                    return 'Retained Percent';
                case 'variable':
                    return 'Variable';
                case 'percentage_of_gross_margin':
                    return '% of Gross Margin'
                case 'percentage_of_net_margin':
                    return '% of Net Margin'
                case 'fixed':
                    return 'Fixed'
            }
        },
        isRateOffer(type) {
            return type === 'rate';
        },
        isFlatFeeOffer(type) {
            return type === 'flat_fee';
        },
        isPercentageOfMarginOffer(type) {
            return type === 'percentage_of_margin';
        },
        isRetainedPercentOffer(type) {
            return type === 'retained_percent';
        },
        toggleEdit() {
            this.isEditActive = !this.isEditActive;
        },
        cancelEdit() {
            this.parseMarketSheetValues();
            this.toggleEdit();
        },
        getIsEditableStatus(status) {
            return ['requested', 'expired'].includes(status);
        },
        getPercentageOfMarginOffer (offer) {
            if (this.risk.net_margin !== null) {
                return this.$options.filters.numberNoTrail(offer.price) + ' BPS';
            }

            return `${offer.percentage_of_margin}%  (Min: ${offer.minimum_price} BPS)`;
        },
        async saveMRSChanges() {
            let mrsData = {data: this.marketSheetValues.map(offer => {
                return {
                    id: offer.id,
                    market_response_status: offer.market_response_status,
                    line_size_amount: offer.line_size_amount,
                    type: offer.type,
                    percentage_of_margin: offer.percentage_of_margin,
                    flat_fee: offer.flat_fee,
                    indicated_line_size: offer.indicated_line_size,
                    indicated_line_size_currency_id: offer.indicated_line_size_currency_id,
                    price: parseFloat(offer.price)
                }
            })};
            await axios.put(`/api/broker/submission/${this.risk.risk_id}/market-sheet`, mrsData);
            this.$router.go(0);
        },
        openFeedbackModal(id) {
            this.activeOffer = this.marketSheetValues.find(offer => offer.id === id);
            this.$nextTick(() => {
                this.$refs.feedbackModal.open();
            });
        },
        openSubjectivitiesModal(id) {
            this.activeOffer = this.marketSheetValues.find(offer => offer.id === id);
            this.$nextTick(() => {
                this.$refs.subjectivitiesModal.open();
            });
        },
        async downloadMarketSheet(documentType) {
            try {
                this.isDocumentCurrentlyDownloading = true;
                const fileUrl = `broker/submission/${this.risk.risk_id}/market-sheet/${documentType}/download`;

                window.location.href = (
                    await axios.get(this.$root.api(fileUrl))
                ).data.url;
            } catch ($error) {

            } finally {
                this.isDocumentCurrentlyDownloading = false;
            }
        },
        updateFeedback(feedback) {
            this.marketSheetValues.find(offer => offer.id === this.activeOffer.id).feedback = feedback;
        },
        subjectivitiesUpdated(subjectivities) {
            this.marketSheetValues.find(offer => offer.id === this.activeOffer.id).subjectivities = subjectivities;
        }
    }
}
</script>

<style lang="scss" scoped>
.x-card .x-card__title .x-btn.secondary i {
    font-size: 18px;
}

.x-table.x-datatable tbody tr td:not(.note-cell) {
    vertical-align: middle;
}

.x-table.x-datatable tbody tr td.note-cell {
    height: auto;
    padding-top: 0;
    padding-bottom: 0;
    color: $color-body-text;

    > p span:last-child {
        display: inline-block;
        padding-bottom: 8px;
    }
}

.line-size-currency {
    width: 35%;
    margin-right: 5%;
}

.line-size-amount {
    width: 60%;
}

.counter {
    position: relative;
    z-index: 1;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    vertical-align: middle;
    min-width: 24px;
    min-height: 24px;
    background: $color-gold;
    color: $color-white;
    margin-left: -10px;
    border-radius: 12px;
    padding: 2px 4px;
    font-size: rem(16px);
    font-weight: 600;

    &.white {
        background: $color-grey-3;
        color: $color-grey-10;
    }
}

.x-tag {
    width: 100%;
    text-align: center;
}

.unset-align tbody tr td {
    vertical-align: unset;
}
</style>
